// idleSession.js
//
// Framework-agnostic idle-session tracker that works across tabs.
//
// The ONLY shared state is one absolute timestamp in localStorage: the moment the
// session dies if nobody does anything. Every tab derives its state from it:
//
//   remaining = expiresAt - now
//   remaining >  graceMs   -> 'active'
//   remaining <= graceMs   -> 'warning'   (show the dialog)
//   remaining <= 0         -> expired     (log out)
//
// Activity in any tab pushes the deadline forward. STAY pushes it forward.
// Logout removes the key, which makes every other tab expire as well.
// There are no messages, no election and no "warning started" flag.

const KEY = 'idle-session:expiresAt';
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
// capture: true so components that call stopPropagation() can't hide activity
const LISTENER_OPTS = { passive: true, capture: true };

export function createIdleSession({
  idleMs = 30 * 60 * 1000,            // inactivity before the warning
  graceMs = 60 * 1000,                // time to answer the warning
  writeThrottleMs = 5 * 1000,         // max one localStorage write per 5 s of activity
  tickMs = 1000,
  activityDismissesWarning = false,   // true: any input during the warning counts as "stay"
  onChange = () => {},                // (status: 'active' | 'warning', remainingMs) => void
  onExpire = () => {},
} = {}) {
  let status = 'active';
  let lastWrite = 0;
  let stopped = false;
  let timer = null;

  // Missing or garbage value => 0 => treated as expired.
  // That is what makes "another tab logged out" work without any messaging.
  const readDeadline = () => {
    const v = Number(localStorage.getItem(KEY));
    return Number.isFinite(v) ? v : 0;
  };

  const extend = () => {
    lastWrite = Date.now();
    localStorage.setItem(KEY, String(lastWrite + idleMs + graceMs));
  };

  function evaluate() {
    if (stopped) return;
    const remaining = readDeadline() - Date.now();
    if (remaining <= 0) {
      expire();
      return;
    }
    status = remaining <= graceMs ? 'warning' : 'active';
    onChange(status, remaining);
  }

  function onActivity() {
    if (stopped) return;
    if (status === 'warning') {
      if (!activityDismissesWarning) return;   // only an explicit STAY counts
      extend();
      evaluate();
      return;
    }
    if (Date.now() - lastWrite >= writeThrottleMs) extend();
  }

  // Fires in OTHER tabs when the deadline changes or is removed.
  const onStorage = (e) => {
    if (e.key === KEY || e.key === null) evaluate();   // null = localStorage.clear()
  };

  // Hidden tabs have their timers throttled; re-check the moment the tab is shown.
  const onVisibility = () => {
    if (!document.hidden) evaluate();
  };

  function teardown() {
    stopped = true;
    clearInterval(timer);
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity, LISTENER_OPTS));
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisibility);
  }

  function expire() {
    if (stopped) return;
    teardown();
    localStorage.removeItem(KEY);   // other tabs see the removal and expire too
    onExpire();
  }

  // ---- start ----
  ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, LISTENER_OPTS));
  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', onVisibility);
  // Starting (a login or a page load) is itself user activity, so always begin
  // with a fresh deadline. This also means no state from an earlier session
  // can ever leak into a new one.
  extend();
  timer = setInterval(evaluate, tickMs);
  evaluate();

  return {
    /** User clicked STAY. Call after the server session has been refreshed. */
    stay() {
      if (stopped) return;
      extend();
      evaluate();
    },
    /** Explicit logout (button, 401 interceptor, ...). Logs out every other tab too. Idempotent. */
    end() {
      if (stopped) return;
      teardown();
      localStorage.removeItem(KEY);
    },
  };
}
